import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { AxiosError, AxiosRequestConfig } from "axios";
import Cookies from "js-cookie";
import { useState } from "react";
import { api } from "../../../../shared/services/api";
import { toast } from "react-toastify";
import { maskCPFCNPJ, maskOnlyNumbers } from "../../../../shared/utils/mask";
import { IResponseWhiteList } from "../../../../shared/services/types/response";
import { useEffect } from "react";

const style = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "60%",
  height: "80%",
  bgcolor: "background.paper",
  border: "2px solid #cecece",
  borderRadius: 5,
  boxShadow: 24,
  p: 4,
};

interface ModalDetailProps {
  open: boolean;
  closeModal: (shouldReload?: boolean) => void;
  whenCreatedSuccess: () => void;
  whitelistData: IResponseWhiteList | null;
  clearEditData: () => void;
}

const ModalWhitelist = ({ open, whitelistData, clearEditData, closeModal, whenCreatedSuccess }: ModalDetailProps) => {
  const token = Cookies.get("@token");
  const [clientName, setClientName] = useState<string>("");
  const [document, setDocument] = useState<string>("");
  const [agency, setAgency] = useState<string>("");
  const [account, setAccount] = useState<string>("");
  const [digit, setDigit] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Estado para armazenar os erros de validação
  const [errors, setErrors] = useState({
    clientName: "",
    document: "",
    agency: "",
    account: "",
    digit: "",
  });

  // Validação simples: todos os campos devem ser preenchidos
  const validateForm = () => {
    const newErrors = {
      clientName: clientName?.trim() ? "" : "Campo obrigatório",
      document: document?.trim() ? "" : "Campo obrigatório",
      agency: agency?.trim() ? "" : "Campo obrigatório",
      account: account?.trim() ? "" : "Campo obrigatório",
      digit: digit?.trim() ? "" : "Campo obrigatório",
    };

    setErrors(newErrors);

    // Se algum campo tiver erro, retorne false
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const clearFields = () => {
    setDocument("");
    setClientName("");
    setAccount("");
    setDigit("");
    setAgency("");
  }

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const config: AxiosRequestConfig = {
      headers: {
        Authorization: "Bearer " + token,
        "Content-type": "application/json",
        Accept: "application/json",
      }
    };
    setIsLoading(true);
    try {
      const documentNumber = document.replace(/[^\d]/g, '');

      const response = await api.post(`/whitelist`, {
        cnpj_cpf_client: documentNumber,
        name_client: clientName,
        account,
        digit,
        agency
      }, config)

      if (response.status === 201) {
        toast.success("Registro salvo com sucesso");
        clearFields();
        whenCreatedSuccess();
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errorMessage = err.response?.data?.message || "Erro de conexão";
        toast.error(errorMessage);
      } else {
        toast.error("Erro inesperado");
      }
    }
    setIsLoading(false);
    closeModal(true);
  };

  const handleSubmitEdit = async () => {
    if (!validateForm()) return;

    const config: AxiosRequestConfig = {
      headers: {
        Authorization: "Bearer " + token,
        "Content-type": "application/json",
        Accept: "application/json",
      }
    };
    setIsLoading(true);
    try {
      const documentNumber = document.replace(/[^\d]/g, '');

      const response = await api.put(`/whitelist/updateWhitelist/${whitelistData?.id}`, {
        cnpj_cpf_client: documentNumber,
        name_client: clientName,
        account,
        digit,
        agency
      }, config)

      if (response.status === 201) {
        toast.success("Registro editado com sucesso");
        clearFields();
        whenCreatedSuccess();
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errorMessage = err.response?.data?.message || "Erro de conexão";
        toast.error(errorMessage);
      } else {
        toast.error("Erro inesperado");
      }
    }
    setIsLoading(false);
    closeModal(true);
  };

  const handleCloseModal = () => {
    closeModal();
    clearFields();
    if (whitelistData !== undefined) {
      clearEditData();
    }
  }

  useEffect(() => {
    if (whitelistData) {
      setClientName(whitelistData.name_client);
      setDocument(maskCPFCNPJ(whitelistData.cnpj_cpf_client));
      //setDocument(whitelistData.cnpj_cpf_client);
      setAgency(whitelistData.agency);
      setAccount(whitelistData.account);
      setDigit(whitelistData.digit);
    } else {
      // Resetar campos se não houver dados (modo de criação)
      clearFields();
    }
  }, [whitelistData]); // 

  return (
    <Modal
      open={open}
      onClose={() => handleCloseModal()}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={{ ...style, position: "relative" }}>
        <Typography id="modal-title" variant="h6" component="h2" gutterBottom>
          {whitelistData ? "Editar" : "Adicionar"} Whitelist
        </Typography>
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 2,
          }}
          noValidate
          autoComplete="off"
        >
          <TextField
            required
            label="Nome do Cliente"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            error={Boolean(errors.clientName)}
            helperText={errors.clientName}
            fullWidth
          />
          <TextField
            required
            label="Documento"
            value={document}
            onChange={(e) => setDocument(maskCPFCNPJ(e.target.value))}
            //type="number"
            error={Boolean(errors.document)}
            helperText={errors.document}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 18 } }}
          />
          <TextField
            required
            label="Agência"
            value={agency}
            onChange={(e) => setAgency(maskOnlyNumbers(e.target.value))}
            error={Boolean(errors.agency)}
            helperText={errors.agency}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 5 } }}
          />
          <TextField
            required
            label="Conta"
            value={account}
            onChange={(e) =>  setAccount(maskOnlyNumbers(e.target.value))}
            slotProps={{ htmlInput: { maxLength: 5 } }}
            error={Boolean(errors.account)}
            helperText={errors.account}
            fullWidth
          />
          <TextField
            required
            label="Dígito"
            value={digit}
            onChange={(e) => {
              setDigit(maskOnlyNumbers(e.target.value))
            }}
            //type="number"
            slotProps={{ htmlInput: { maxLength: 1 } }}
            error={Boolean(errors.digit)}
            helperText={errors.digit}
            fullWidth
          />
        </Box>
        {/* Botões fixados no canto inferior direito */}
        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
            display: "flex",
            gap: 2,
          }}
        >
          <Button
            variant="contained"
            color="error"
            onClick={() => handleCloseModal()}
          >
            Cancelar
          </Button>
          {whitelistData ? (
            <Button variant="contained" disabled={isLoading} color="primary" onClick={handleSubmitEdit}>
              Editar
            </Button>
          ) : (
            <Button variant="contained" disabled={isLoading} color="primary" onClick={handleSubmit}>
              Enviar
            </Button>
          )}

        </Box>
      </Box>
    </Modal>
  );
};

export default ModalWhitelist;